import { useId, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { AddRounded } from '@mui/icons-material';
import { Box, Button, Divider, MenuItem, Stack } from '@mui/material';
import { AnchoredMenu, Field, FormTextField, createInputSurfaceStyles, designTokens } from '../../../design-system';
import type { ProfileMappingCandidate, SourcePropertyDefinition, TargetFieldDefinition } from '../model/types';
import { tokenizeFormatText } from '../model/mappingFormatTokens';
import { readFormatDom, readFormatSelection, renderFormatDom, restoreFormatSelection } from './mappingFormatDom';

type Selection = { start: number; end: number };
type Snapshot = { text: string; selection: Selection };
type AttributeMenu = { anchor: HTMLElement; selection: Selection; sourceProperty?: string };

export function MappingValueFormatEditor({ index, candidate, text, error, target, sources, disabled, canAddSource, excludedSources, onTextChange, onCandidateChange, registerInput }: {
  index: number;
  candidate: ProfileMappingCandidate;
  text: string;
  error?: string;
  target: TargetFieldDefinition;
  sources: SourcePropertyDefinition[];
  disabled: boolean;
  canAddSource: boolean;
  excludedSources: string[];
  onTextChange: (text: string) => void;
  onCandidateChange: (candidate: ProfileMappingCandidate) => void;
  registerInput: (key: string, element: { focus: () => void } | null) => void;
}) {
  const id = useId();
  const input = useRef<HTMLDivElement | null>(null);
  const renderedText = useRef<string | undefined>(undefined);
  const selection = useRef<Selection>({ start: text.length, end: text.length });
  const beforeInput = useRef<Selection>(selection.current);
  const composing = useRef(false);
  const history = useRef<{ past: Snapshot[]; future: Snapshot[] }>({ past: [], future: [] });
  const [selectedText, setSelectedText] = useState('');
  const [menu, setMenu] = useState<AttributeMenu | null>(null);
  const options = sources.filter((source) => !excludedSources.includes(source.key));
  const textRef = useRef(text);
  textRef.current = text;

  const rememberSelection = () => {
    const next = input.current && readFormatSelection(input.current);
    if (!next) return;
    selection.current = next;
    setSelectedText(textRef.current.slice(next.start, next.end));
  };
  const render = (next: string, nextSelection?: Selection) => {
    if (!input.current) return;
    renderFormatDom(input.current, next, sources);
    renderedText.current = next;
    if (nextSelection) {
      selection.current = nextSelection;
      restoreFormatSelection(input.current, nextSelection);
    }
  };
  useLayoutEffect(() => {
    if (!input.current || composing.current) return;
    if (renderedText.current !== text) render(text);
    input.current.querySelectorAll<HTMLButtonElement>('[data-format-token]').forEach((button) => { button.disabled = disabled; });
  });
  useLayoutEffect(() => {
    if (index === 0 && target.supportsValueTemplates) input.current?.focus();
    // 처음 편집을 시작할 때만 초점을 이동합니다. 순서 변경의 초점은 상위에서 처리합니다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (next: string, nextSelection: Selection, previousSelection = selection.current) => {
    if (disabled) return;
    if (next !== textRef.current) {
      history.current.past.push({ text: textRef.current, selection: previousSelection });
      if (history.current.past.length > 100) history.current.past.shift();
      history.current.future = [];
    }
    render(next, nextSelection);
    textRef.current = next;
    onTextChange(next);
    setSelectedText(next.slice(nextSelection.start, nextSelection.end));
  };
  const replaceSelection = (replacement: string, range = selection.current) => {
    const next = textRef.current.slice(0, range.start) + replacement + textRef.current.slice(range.end);
    const caret = range.start + replacement.length;
    commit(next, { start: caret, end: caret }, range);
  };
  const restoreHistory = (redo: boolean) => {
    if (disabled) return;
    const from = redo ? history.current.future : history.current.past;
    const to = redo ? history.current.past : history.current.future;
    const previous = from.pop();
    if (!previous) return;
    to.push({ text: textRef.current, selection: selection.current });
    render(previous.text, previous.selection);
    textRef.current = previous.text;
    onTextChange(previous.text);
    input.current?.focus();
  };
  const closeMenu = () => {
    const nextSelection = menu?.selection ?? selection.current;
    menu?.anchor.setAttribute('aria-expanded', 'false');
    setMenu(null);
    requestAnimationFrame(() => {
      input.current?.focus();
      if (input.current) restoreFormatSelection(input.current, nextSelection);
    });
  };
  const applySource = (key?: string) => {
    if (disabled || !menu) return;
    const source = options.find((option) => option.key === key);
    if (key && !source) return;
    const token = source ? '{{' + source.label + '}}' : '';
    const nextSelection = { start: menu.selection.start + token.length, end: menu.selection.start + token.length };
    setMenu(null);
    replaceSelection(token, menu.selection);
    requestAnimationFrame(() => {
      input.current?.focus();
      if (input.current) restoreFormatSelection(input.current, nextSelection);
    });
  };
  const selectToken = (button: HTMLElement) => {
    if (!input.current) return null;
    const range = input.current.ownerDocument.createRange();
    range.selectNode(button);
    const browserSelection = input.current.ownerDocument.getSelection();
    browserSelection?.removeAllRanges();
    browserSelection?.addRange(range);
    const tokenSelection = readFormatSelection(input.current);
    if (!tokenSelection) return null;
    selection.current = tokenSelection;
    return tokenSelection;
  };
  const openTokenMenu = (button: HTMLElement) => {
    if (disabled) return;
    const tokenSelection = selectToken(button);
    if (!tokenSelection) return;
    button.setAttribute('aria-controls', id);
    button.setAttribute('aria-expanded', 'true');
    setMenu({ anchor: button, selection: tokenSelection, sourceProperty: button.dataset.sourceProperty });
  };
  const handleClipboard = (event: ClipboardEvent<HTMLDivElement>, cut: boolean) => {
    rememberSelection();
    const { start, end } = selection.current;
    if (start === end) return;
    event.preventDefault();
    event.clipboardData.setData('text/plain', textRef.current.slice(start, end));
    if (cut && !disabled) replaceSelection('');
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || composing.current || event.nativeEvent.isComposing) return;
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      input.current?.focus();
      if (input.current) restoreFormatSelection(input.current, { start: 0, end: textRef.current.length });
      rememberSelection();
      return;
    }
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-format-token]');
    if (button && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
      event.preventDefault();
      openTokenMenu(button);
      return;
    }
    if (button && ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
      event.preventDefault();
      const tokenSelection = selectToken(button);
      if (!tokenSelection) return;
      const remove = event.key === 'Backspace' || event.key === 'Delete';
      const caret = event.key === 'ArrowRight' ? tokenSelection.end : tokenSelection.start;
      if (remove) replaceSelection('', tokenSelection);
      input.current?.focus();
      if (input.current) restoreFormatSelection(input.current, { start: caret, end: caret });
      return;
    }
    if ((event.metaKey || event.ctrlKey) && (event.key.toLowerCase() === 'z' || event.key.toLowerCase() === 'y')) {
      event.preventDefault();
      rememberSelection();
      restoreHistory(event.shiftKey || event.key.toLowerCase() === 'y');
      return;
    }
    if (event.key === 'Enter') { event.preventDefault(); return; }
    rememberSelection();
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;
    const range = selection.current;
    if (range.start !== range.end) {
      event.preventDefault();
      replaceSelection('');
      return;
    }
    const token = tokenizeFormatText(textRef.current, sources).find((part) => part.kind === 'source' &&
      (event.key === 'Backspace' ? part.end === range.start : part.start === range.start));
    if (token) {
      event.preventDefault();
      replaceSelection('', { start: token.start, end: token.end });
    }
  };
  useLayoutEffect(() => {
    const editor = input.current;
    if (!editor) return;
    const handleBeforeInput = (event: InputEvent) => {
      beforeInput.current = readFormatSelection(editor) ?? selection.current;
      if (disabled || event.inputType === 'insertParagraph' || event.inputType === 'insertLineBreak') event.preventDefault();
      if (event.inputType === 'historyUndo' || event.inputType === 'historyRedo') {
        event.preventDefault();
        restoreHistory(event.inputType === 'historyRedo');
      }
    };
    editor.addEventListener('beforeinput', handleBeforeInput);
    return () => editor.removeEventListener('beforeinput', handleBeforeInput);
  });

  if (!target.supportsValueTemplates) return <FormTextField select autoFocus={index === 0} label={(index + 1) + '번째 값의 직원 속성'} value={candidate.sourceProperties[0] ?? ''} disabled={disabled}
    inputRef={(element: { focus: () => void } | null) => { registerInput(index + ':0', element); registerInput(index + ':add', element); }}
    slotProps={{ select: { displayEmpty: true } }}
    onChange={(event) => onCandidateChange({ sourceProperties: event.target.value ? [event.target.value] : [] })}>
    <MenuItem value="">연결하지 않음</MenuItem>
    {options.map((source) => <MenuItem key={source.key} value={source.key}>{source.label} · {source.category}</MenuItem>)}
  </FormTextField>;

  return <Stack spacing={1}>
    <Field.Root disabled={disabled} invalid={Boolean(error)}>
      <Field.Control>{(state) => <Box
        id={state.controlId} role="textbox" aria-label={(index + 1) + '번째 값의 표시 형식'} aria-multiline="false"
        aria-describedby={state.messageId} aria-invalid={Boolean(error)} aria-disabled={disabled || undefined}
        contentEditable={!disabled} suppressContentEditableWarning spellCheck={false} tabIndex={disabled ? -1 : 0}
        ref={(element: HTMLDivElement | null) => {
          input.current = element;
          registerInput(index + ':0', element);
          registerInput(index + ':add', element);
        }}
        data-placeholder="문자를 입력하거나 속성을 삽입하세요"
        onInput={() => {
          if (!input.current || composing.current) return;
          commit(readFormatDom(input.current), readFormatSelection(input.current) ?? selection.current, beforeInput.current);
        }}
        onFocus={rememberSelection} onKeyUp={rememberSelection} onMouseUp={rememberSelection} onBlur={rememberSelection}
        onClick={(event) => {
          const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-format-token]');
          if (button) openTokenMenu(button);
        }}
        onCompositionStart={() => { composing.current = true; beforeInput.current = selection.current; }}
        onCompositionEnd={() => {
          composing.current = false;
          if (input.current) commit(readFormatDom(input.current), readFormatSelection(input.current) ?? selection.current, beforeInput.current);
        }}
        onKeyDown={handleKeyDown}
        onCopy={(event) => handleClipboard(event, false)} onCut={(event) => handleClipboard(event, true)}
        onPaste={(event) => {
          event.preventDefault();
          if (disabled) return;
          rememberSelection();
          replaceSelection(event.clipboardData.getData('text/plain'));
        }}
        onDrop={(event) => event.preventDefault()}
        sx={{
          ...createInputSurfaceStyles({ disabled, invalid: Boolean(error) }),
          ...designTokens.typography.ui,
          minHeight: 76, p: `${designTokens.space.sm}px`, bgcolor: designTokens.color.canvas,
          color: disabled ? designTokens.color.disabledText : designTokens.color.primaryText,
          whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', outline: 'none', lineHeight: '2.5rem', cursor: disabled ? 'default' : 'text',
          '&:empty::before': { content: 'attr(data-placeholder)', color: designTokens.color.mutedText, pointerEvents: 'none' },
          '& [data-format-token]': {
            font: 'inherit', lineHeight: '1.25rem', display: 'inline-flex', alignItems: 'center', verticalAlign: 'baseline',
            gap: `${designTokens.space.xxs}px`, px: `${designTokens.space.xs}px`, py: `${designTokens.space.xxs}px`, mx: '2px',
            minHeight: 32, maxWidth: '100%', border: `1px solid ${designTokens.color.brandBorder}`, borderRadius: designTokens.radius.control,
            bgcolor: designTokens.color.compactSelectedSurface, color: designTokens.color.actionDark, cursor: 'pointer',
            '&::after': { content: '"▾"', fontSize: '0.75rem' },
            '&:hover:not(:disabled)': { bgcolor: designTokens.color.selectedHoverSurface },
            '&:focus-visible': { outline: `2px solid ${designTokens.color.action}`, outlineOffset: 2 },
            '&:disabled': { cursor: 'default', opacity: designTokens.button.disabledOpacity },
            '@media (pointer: coarse)': { minHeight: 44 },
            '@media (forced-colors: active)': { borderColor: 'ButtonText', color: 'ButtonText', '&:focus-visible': { outlineColor: 'Highlight' } },
          },
          '@media (pointer: coarse)': { fontSize: '1rem', lineHeight: '3rem' },
        }}
      />}</Field.Control>
      <Field.Message>{error}</Field.Message>
    </Field.Root>
    <Button size="small" variant="text" startIcon={<AddRounded />}
      disabled={disabled || options.length === 0 || (!canAddSource && !selectedText.includes('{{'))}
      aria-label={(index + 1) + '번째 값에 속성 삽입'} aria-haspopup="menu" aria-expanded={Boolean(menu && !menu.sourceProperty)}
      aria-controls={menu ? id : undefined} onMouseDown={(event) => event.preventDefault()}
      onClick={(event) => setMenu({ anchor: event.currentTarget, selection: selection.current })}
      sx={{ alignSelf: 'flex-start' }}>속성 삽입</Button>
    <AnchoredMenu id={id} anchorEl={menu?.anchor ?? null} onClose={closeMenu} ariaLabel="직원 속성 선택">
      {options.map((source) => <MenuItem key={source.key} role="menuitemradio" aria-checked={source.key === menu?.sourceProperty} selected={source.key === menu?.sourceProperty} disabled={disabled} onClick={() => applySource(source.key)}>{source.label} · {source.category}</MenuItem>)}
      {menu?.sourceProperty && <Divider />}
      {menu?.sourceProperty && <MenuItem disabled={disabled} onClick={() => applySource()}>속성 삭제</MenuItem>}
    </AnchoredMenu>
  </Stack>;
}
